import React, { useContext } from 'react';
import { flowRight } from 'lodash';
import AiChatBaseTemplate from '../../components/ai-chat-base-template/AiChatBaseTemplate.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { ELSIcon } from '@els/els-component-form-field-react';
import { RecordEmpty } from '../../models/common.models';
import { getBrandName } from '../ai-chat/ai-chat.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import { RoutePath } from '../../components/app/app.constants';
import { preserveAdminOverrideParam } from '../../utilities/app.utilities';
import { useHistory } from 'react-router';
import { Footer } from '@els/els-react--footer';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';

const HowItWorksComponent = (): JSX.Element => {
  const {
    appState,
  } = useContext(AppContext);
  const history = useHistory();
  const hideNav = appState.appLinkData?.outPostBody?.hideNav

  return (
    <AiChatBaseTemplate
      isPanelOpen={false}
      panelContent={null}
    >
      <div className="c-naa-base-page">
        <div className="c-naa-base-page__header">
          <div className="c-naa-base-page__inner">
            <h3>How it works</h3>
            {hideNav && (
              <div className="u-els-margin-top">
                <button
                  type="button"
                  className="u-els-anchorize"
                  onClick={() => {
                    history.push(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
                  }}
                >
                  Return to chat
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="c-naa-base-page__body">
          <div className="c-naa-base-page__inner">

            <div className="o-els-container">
              <div className="c-els-card">
                <div className="c-els-card__content">
                  <div className="u-els-margin-bottom">
                    <h4>{getBrandName(appState)} pointers</h4>
                  </div>
                  <div className="o-els-container o-els-container--1o2">
                    {getBrandName(appState)}
                  </div>
                  <ul className="c-els-list">
                    <li className="c-els-list__item">Searches your textbooks using the topics in your message</li>
                    <li className="c-els-list__item">Cannot search by chapter or section keywords</li>
                    <li className="c-els-list__item">Does not have access to your course assignments or other materials</li>
                  </ul>

                  <div className="o-els-container o-els-container--1x1o2">
                    <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_1o2, FlexLayoutModifier.MIDDLE]}>
                      <FlexItem>
                        <ELSIcon
                          size="1x1o4"
                          prefix="hmds"
                          name="checkmark"
                          customClass="u-els-color-confirm u-els-display-block"
                        />
                      </FlexItem>
                      <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                        <h4 className="">{getBrandName(appState)} <strong>can</strong> answer:</h4>
                      </FlexItem>
                    </FlexLayout>
                    <div className="c-els-ai-chat__explainer-examples">
                      <p className="o-els-container o-els-container--1o2">&quot;What are the signs and symptoms of congestive heart failure?&quot;</p>
                      <p>&quot;How do beta blockers work to manage hypertension?&quot;</p>
                    </div>
                  </div>

                  <div className="">
                    <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_1o2, FlexLayoutModifier.MIDDLE]}>
                      <FlexItem>
                        <ELSIcon
                          size="1x1o4"
                          prefix="hmds"
                          name="close"
                          customClass="u-els-color-warn u-els-display-block"
                        />
                      </FlexItem>
                      <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                        <h4>{getBrandName(appState)} <strong>cannot</strong> answer:</h4>
                      </FlexItem>
                    </FlexLayout>
                    <div className="c-els-ai-chat__explainer-examples">
                      <p className="o-els-container o-els-container--1o2">&quot;What is in chapter 2?&quot;</p>
                      <p>&quot;When is my next assignment due?&quot;</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h4 className="o-els-container o-els-container--1x1o2">What is {getBrandName(appState)}?</h4>
            <p className="o-els-container">
              {getBrandName(appState)} is a conversational tool powered by generative AI and designed to
              support health professions learners and educators by delivering trusted, evidence-based
              responses and content to your questions.
            </p>

            <p className="o-els-container">
              {getBrandName(appState)} features are continuously improved using your feedback.
            </p>

            <h4 className="o-els-container o-els-container--1x1o2">What you need to know</h4>

            <p className="o-els-container">
              {getBrandName(appState)} leverages authored content from the textbooks and relevant materials
              available to you, and every response includes a list of the sources used to generate it.
              Depending on the materials in your account, {getBrandName(appState)} might also provide
              supplemental Osmosis videos and images from textbooks. It uses AI to generate responses in
              real time, without human author review.&nbsp;
              <strong>
                This means that {getBrandName(appState)} may provide variable results, and accuracy should
                be verified using the source content. The AI also does not fully support math or medication
                dosage calculations.
              </strong>
              &nbsp;Clinical judgment and discretion should be exercised before acting on or applying the
              information.
            </p>

            <p className="o-els-container">
              Queries made to {getBrandName(appState)} may be reviewed to improve the AI&apos;s
              performance.&nbsp;
              <strong>
                Do not enter personal, confidential, or sensitive information into {getBrandName(appState)}.
              </strong>
            </p>

            <p className="o-els-container">
              <strong>We want your feedback!</strong>
              &nbsp;Use the thumbs-up and thumbs-down emojis on any response and click the Submit feedback
              button. Health professions experts will also evaluate responses to drive continuous
              improvement.
            </p>

            <div className="u-els-margin-top-2x">
              <Footer
                legalEntity="Elsevier Inc."
              >
                <div>
                  <a href="https://service.elsevier.com/app/answers/detail/a_id/11544/supporthub/evolve/kw/accessibility/"
                    target="_blank" rel="noreferrer">
                    Evolve Accessibility Support Hub</a>
                </div>
                <div>
                  <a href="https://service.elsevier.com/app/answers/detail/a_id/38510/supporthub/sherpath/"
                    target="_blank" rel="noreferrer">
                    Accessibility Statement
                  </a>
                </div>
              </Footer>
            </div>

          </div>
        </div>


      </div>
    </AiChatBaseTemplate>
  );
};

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'How It Works' }),
);

const HowItWorks: React.FC<RecordEmpty> = enhance(HowItWorksComponent);

export default HowItWorks;
